import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/lib/auth-context';
import { invoicesAPI, firmsAPI, paymentsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { IndianRupee, Search, ArrowUp, ArrowDown, ArrowUpDown, ArrowLeft, CreditCard, Calendar, Building2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const SortIcon = ({ col, sort }) => {
  if (sort.col !== col) return <ArrowUpDown className="h-3 w-3 text-muted-foreground/40" />;
  return sort.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
};

const STATUS_STYLE = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-amber-100 text-amber-700',
  partially_paid: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
};

export default function PendingFeesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [allInvoices, setAllInvoices] = useState([]);
  const [firms, setFirms] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [sort, setSort] = useState({ col: 'invoice_number', dir: 'asc' });
  const [loading, setLoading] = useState(true);

  // Payment dialog state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', bank_account_id: '', transfer_date: '' });
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const loadInvoices = useCallback(() => {
    invoicesAPI.list()
      .then(res => {
        const unpaid = (res.data || []).filter(inv => inv.status === 'draft' || inv.status === 'sent' || inv.status === 'partially_paid');
        setAllInvoices(unpaid);
      })
      .catch(err => logger.error('Failed to load invoices', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadInvoices();
    firmsAPI.list()
      .then(res => setFirms(res.data || []))
      .catch(() => {});
  }, [loadInvoices]);

  const clients = useMemo(() => {
    const map = {};
    for (const inv of allInvoices) {
      if (inv.client_id && inv.client_name) map[inv.client_id] = inv.client_name;
    }
    return Object.entries(map).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allInvoices]);

  const filteredItems = useMemo(() => {
    let result = allInvoices;
    if (statusFilter !== 'all') result = result.filter(i => i.status === statusFilter);
    if (clientFilter !== 'all') result = result.filter(i => i.client_id === clientFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(i =>
        (i.invoice_number || '').toLowerCase().includes(q) ||
        (i.client_name || '').toLowerCase().includes(q) ||
        (i.service_description || '').toLowerCase().includes(q)
      );
    }
    const { col, dir } = sort;
    return [...result].sort((a, b) => {
      if (col === 'amount') {
        const va = Number(a.invoice_value || a.amount || 0);
        const vb = Number(b.invoice_value || b.amount || 0);
        return dir === 'asc' ? va - vb : vb - va;
      }
      const va = (a[col] || '').toString().toLowerCase();
      const vb = (b[col] || '').toString().toLowerCase();
      return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }, [allInvoices, statusFilter, clientFilter, searchQuery, sort]);

  const totalOutstanding = useMemo(() =>
    filteredItems.reduce((s, i) => s + Number(i.invoice_value || i.amount || 0) - Number(i.amount_paid || 0), 0)
  , [filteredItems]);

  const statusCounts = useMemo(() => {
    const counts = { draft: 0, sent: 0, partially_paid: 0 };
    for (const inv of allInvoices) counts[inv.status] = (counts[inv.status] || 0) + 1;
    return counts;
  }, [allInvoices]);

  const toggleSort = (col) => {
    setSort(prev => {
      if (prev.col === col) return { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      return { col, dir: 'asc' };
    });
  };

  const openPaymentDialog = async (inv, e) => {
    e.stopPropagation();
    setSelectedInvoice(inv);
    const invoiceTotal = Number(inv.invoice_value || inv.amount || 0);
    const paid = Number(inv.amount_paid || 0);
    setPaymentForm({
      amount: String(Math.max(0, invoiceTotal - paid)),
      bank_account_id: '',
      transfer_date: new Date().toISOString().split('T')[0],
    });
    setPaymentDialogOpen(true);
    // Load payment history
    try {
      const res = await paymentsAPI.list({ invoice_id: inv.id });
      setPaymentHistory(res.data || []);
    } catch { setPaymentHistory([]); }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!selectedInvoice) return;
    const amt = parseFloat(paymentForm.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (!paymentForm.transfer_date) { toast.error('Select transfer date'); return; }

    // Find firm and bank account label
    const firm = firms.find(f => f.id === selectedInvoice.firm_id);
    let bankLabel = '';
    if (paymentForm.bank_account_id && firm) {
      const accounts = firm.bank_accounts || [];
      const ba = accounts.find(a => a.id === paymentForm.bank_account_id);
      if (ba) bankLabel = `${ba.bank_name} - ${ba.account_number}`;
    }

    setSubmitting(true);
    try {
      await paymentsAPI.record({
        invoice_id: selectedInvoice.id,
        amount: amt,
        firm_id: selectedInvoice.firm_id || '',
        bank_account_id: paymentForm.bank_account_id || '',
        bank_account_label: bankLabel,
        transfer_date: paymentForm.transfer_date,
      });
      toast.success('Payment recorded successfully');
      setPaymentDialogOpen(false);
      loadInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    }
    setSubmitting(false);
  };

  const getInvoiceFirm = (inv) => firms.find(f => f.id === inv?.firm_id);
  const getBalance = (inv) => {
    const total = Number(inv.invoice_value || inv.amount || 0);
    return Math.max(0, total - Number(inv.amount_paid || 0));
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-muted rounded-xl animate-pulse" />)}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
        </Button>
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <IndianRupee className="h-5 w-5 text-rose-600" />
            Pending Fees (Receivables)
            <Badge variant="secondary" className="ml-1 bg-rose-100 text-rose-700">{filteredItems.length}</Badge>
          </h2>
          <p className="text-sm text-muted-foreground">Unpaid invoices — draft, sent, and partially paid</p>
        </div>
      </div>

      {/* Summary strip */}
      <div className={`grid grid-cols-2 ${isAdmin ? 'sm:grid-cols-5' : 'sm:grid-cols-4'} gap-3`}>
        <div className={`rounded-lg p-3 text-center border cursor-pointer transition-all ${statusFilter === 'all' ? 'ring-2 ring-primary bg-primary/5' : 'bg-muted/30 hover:bg-muted/50'}`} onClick={() => setStatusFilter('all')} data-testid="fees-filter-all">
          <p className="text-xl font-bold">{allInvoices.length}</p>
          <p className="text-[10px] font-medium text-muted-foreground">All</p>
        </div>
        <div className={`rounded-lg p-3 text-center border cursor-pointer transition-all ${statusFilter === 'draft' ? 'ring-2 ring-primary bg-primary/5' : 'bg-muted/30 hover:bg-muted/50'}`} onClick={() => setStatusFilter(statusFilter === 'draft' ? 'all' : 'draft')} data-testid="fees-filter-draft">
          <p className="text-xl font-bold">{statusCounts.draft}</p>
          <p className="text-[10px] font-medium text-muted-foreground">Draft</p>
        </div>
        <div className={`rounded-lg p-3 text-center border cursor-pointer transition-all ${statusFilter === 'sent' ? 'ring-2 ring-primary bg-primary/5' : 'bg-muted/30 hover:bg-muted/50'}`} onClick={() => setStatusFilter(statusFilter === 'sent' ? 'all' : 'sent')} data-testid="fees-filter-sent">
          <p className="text-xl font-bold">{statusCounts.sent}</p>
          <p className="text-[10px] font-medium text-muted-foreground">Sent</p>
        </div>
        <div className={`rounded-lg p-3 text-center border cursor-pointer transition-all ${statusFilter === 'partially_paid' ? 'ring-2 ring-primary bg-primary/5' : 'bg-muted/30 hover:bg-muted/50'}`} onClick={() => setStatusFilter(statusFilter === 'partially_paid' ? 'all' : 'partially_paid')} data-testid="fees-filter-partial">
          <p className="text-xl font-bold">{statusCounts.partially_paid}</p>
          <p className="text-[10px] font-medium text-muted-foreground">Partial</p>
        </div>
        {isAdmin && (
          <div className="rounded-lg p-3 text-center border bg-rose-50" data-testid="total-outstanding">
            <p className="text-xl font-bold text-rose-700">Rs. {totalOutstanding.toLocaleString('en-IN')}</p>
            <p className="text-[10px] font-medium text-muted-foreground">Outstanding</p>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-[280px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input placeholder="Search invoice, client, service..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-8 h-8 text-xs" data-testid="pending-fees-search" />
        </div>
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="pending-fees-client-filter"><SelectValue placeholder="All Clients" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clients</SelectItem>
            {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {(searchQuery || statusFilter !== 'all' || clientFilter !== 'all') && (
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setSearchQuery(''); setStatusFilter('all'); setClientFilter('all'); }}>Clear</Button>
        )}
        <span className="text-[11px] text-muted-foreground ml-auto">{filteredItems.length} item(s)</span>
      </div>

      {/* Table */}
      {filteredItems.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                {[
                  { key: 'invoice_number', label: 'Invoice #' },
                  { key: 'client_name', label: 'Client' },
                  { key: 'firm_name', label: 'Firm' },
                  { key: 'service_description', label: 'Service' },
                  { key: 'status', label: 'Status' },
                  ...(isAdmin ? [{ key: 'amount', label: 'Amount' }, { key: 'balance', label: 'Balance' }] : []),
                ].map(col => (
                  <TableHead key={col.key} className={`text-xs font-semibold cursor-pointer select-none hover:bg-muted/50 ${(col.key === 'amount' || col.key === 'balance') ? 'text-right' : ''}`} onClick={() => toggleSort(col.key)}>
                    <span className={`flex items-center gap-1 ${(col.key === 'amount' || col.key === 'balance') ? 'justify-end' : ''}`}>
                      {col.label}
                      <SortIcon col={col.key} sort={sort} />
                    </span>
                  </TableHead>
                ))}
                <TableHead className="w-[100px] text-xs font-semibold">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((inv, idx) => {
                const total = Number(inv.invoice_value || inv.amount || 0);
                const paid = Number(inv.amount_paid || 0);
                const balance = Math.max(0, total - paid);
                return (
                  <TableRow key={inv.id || inv.invoice_number} className="hover:bg-muted/30" data-testid={`pending-fee-row-${idx}`}>
                    <TableCell className="text-sm font-mono">{inv.invoice_number}</TableCell>
                    <TableCell className="text-sm font-medium">{inv.client_name || '-'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{inv.firm_name || '-'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{inv.service_description || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={`text-[10px] ${STATUS_STYLE[inv.status] || 'bg-gray-100 text-gray-700'}`}>
                        {inv.status === 'partially_paid' ? 'Partial' : inv.status}
                      </Badge>
                    </TableCell>
                    {isAdmin && (
                      <>
                        <TableCell className="text-sm text-right font-medium">Rs. {total.toLocaleString('en-IN')}</TableCell>
                        <TableCell className="text-sm text-right font-medium text-rose-600">Rs. {balance.toLocaleString('en-IN')}</TableCell>
                      </>
                    )}
                    <TableCell>
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={(e) => openPaymentDialog(inv, e)} data-testid={`record-payment-${idx}`}>
                        <CreditCard className="h-3 w-3" /> Payment
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {isAdmin && (
            <div className="px-4 py-2 border-t text-right text-xs text-muted-foreground">
              Total Outstanding: <span className="font-semibold text-foreground">Rs. {totalOutstanding.toLocaleString('en-IN')}</span>
            </div>
          )}
        </Card>
      ) : (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No pending fees found</CardContent></Card>
      )}

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-emerald-600" />
              Record Payment
            </DialogTitle>
          </DialogHeader>
          {selectedInvoice && (() => {
            const firm = getInvoiceFirm(selectedInvoice);
            const total = Number(selectedInvoice.invoice_value || selectedInvoice.amount || 0);
            const paid = Number(selectedInvoice.amount_paid || 0);
            const balance = getBalance(selectedInvoice);
            const bankAccounts = firm?.bank_accounts || [];
            return (
              <div className="space-y-4">
                {/* Invoice summary */}
                <div className="bg-muted/30 rounded-lg p-3 space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Invoice</span>
                    <span className="font-mono font-medium">{selectedInvoice.invoice_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Client</span>
                    <span className="font-medium">{selectedInvoice.client_name || '-'}</span>
                  </div>
                  {isAdmin && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Invoice Total</span>
                        <span className="font-medium">Rs. {total.toLocaleString('en-IN')}</span>
                      </div>
                      {paid > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Already Paid</span>
                          <span className="font-medium text-green-600">Rs. {paid.toLocaleString('en-IN')}</span>
                        </div>
                      )}
                      <div className="flex justify-between border-t pt-1.5">
                        <span className="text-muted-foreground font-semibold">Balance Due</span>
                        <span className="font-bold text-rose-600">Rs. {balance.toLocaleString('en-IN')}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Firm name (auto) */}
                <div>
                  <Label className="text-xs font-medium">Firm Name</Label>
                  <div className="flex items-center gap-2 mt-1 px-3 py-2 rounded-md border bg-muted/20 text-sm">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span>{firm?.name || 'Not assigned'}</span>
                  </div>
                </div>

                <form onSubmit={handleRecordPayment} className="space-y-4">
                  {/* Bank Account dropdown */}
                  <div>
                    <Label className="text-xs font-medium">Bank Account</Label>
                    {bankAccounts.length > 0 ? (
                      <Select value={paymentForm.bank_account_id} onValueChange={v => setPaymentForm(prev => ({ ...prev, bank_account_id: v }))}>
                        <SelectTrigger className="mt-1" data-testid="payment-bank-account">
                          <SelectValue placeholder="Select bank account" />
                        </SelectTrigger>
                        <SelectContent>
                          {bankAccounts.map(ba => (
                            <SelectItem key={ba.id} value={ba.id}>
                              {ba.bank_name} — {ba.account_number}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-1 px-3 py-2 border rounded-md bg-amber-50">
                        No bank accounts configured for this firm. Add them in Billing &gt; Firms.
                      </p>
                    )}
                  </div>

                  {/* Transfer Date */}
                  <div>
                    <Label className="text-xs font-medium">Date of Transfer *</Label>
                    <div className="relative mt-1">
                      <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="date"
                        value={paymentForm.transfer_date}
                        onChange={e => setPaymentForm(prev => ({ ...prev, transfer_date: e.target.value }))}
                        className="pl-9"
                        required
                        data-testid="payment-transfer-date"
                      />
                    </div>
                  </div>

                  {/* Amount */}
                  <div>
                    <Label className="text-xs font-medium">Amount (Rs.) *</Label>
                    <div className="relative mt-1">
                      <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={paymentForm.amount}
                        onChange={e => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                        className="pl-9"
                        required
                        data-testid="payment-amount"
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full gap-2" disabled={submitting} data-testid="payment-submit">
                    <CheckCircle2 className="h-4 w-4" />
                    {submitting ? 'Recording...' : 'Record Payment'}
                  </Button>
                </form>

                {/* Payment History */}
                {paymentHistory.length > 0 && (
                  <div className="border-t pt-3">
                    <h4 className="text-xs font-semibold text-muted-foreground mb-2">Payment History</h4>
                    <div className="space-y-2">
                      {paymentHistory.map(p => (
                        <div key={p.id} className="flex items-center justify-between text-xs px-3 py-2 bg-green-50 rounded-md border border-green-100">
                          <div>
                            <span className="font-medium">Rs. {Number(p.amount).toLocaleString('en-IN')}</span>
                            <span className="text-muted-foreground ml-2">on {p.transfer_date}</span>
                            {p.bank_account_label && <span className="text-muted-foreground ml-2">via {p.bank_account_label}</span>}
                          </div>
                          <span className="text-muted-foreground">{p.recorded_by_name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
